import autoBind from 'auto-bind'
import axios, { AxiosInstance } from 'axios'
import { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'
import qs from 'qs'

export type ApiBaseProps = {
  headers?: Record<string, string | undefined>
} & AxiosAuthRefreshRequestConfig

export type RequestOptions = {
  fullResponse?: boolean
} & AxiosAuthRefreshRequestConfig

// This is a base API class. It has all needed methods to send request to an api server and proceeds the errors.
// You may create a service which extends or DI (dependency injection) this class.
export class ApiBase {
  public readonly axiosClient: AxiosInstance

  constructor({ headers, timeout = 10000, ...rest }: ApiBaseProps) {
    this.axiosClient = axios.create({
      headers: {
        Accept: 'application/json',
        'content-type': 'application/json',
        ...headers,
      },
      timeout,
      paramsSerializer: params => qs.stringify(params),
      responseType: 'json',
      ...rest,
    })

    autoBind(this)
  }

  async request<TData>({ fullResponse, ...options }: RequestOptions): Promise<TData> {
    return this.axiosClient.request<TData>(options).then(res => res.data)
  }

  async get<TData>(url: string, params?: object | string, options?: RequestOptions) {
    return this.request<TData>({ url, method: 'get', params, ...options })
  }

  async post<TData>(url: string, body?: object, options?: RequestOptions) {
    return this.request<TData>({ url, method: 'post', data: body, ...options })
  }

  async put<TData>(url: string, data?: object, options?: RequestOptions) {
    return this.request<TData>({ url, method: 'put', data, ...options })
  }

  async patch<TData>(url: string, data?: object, options?: RequestOptions) {
    return this.request<TData>({ url, method: 'patch', data, ...options })
  }

  async delete<TData>(url: string, params?: object | string, options?: RequestOptions) {
    return this.request<TData>({ url, method: 'delete', data: params, ...options })
  }
}
